import { Box, Button, Center, Flex, Heading, Text } from "@chakra-ui/react";
import { Markup } from "interweave";
import { isEmpty } from "lodash";

import { useRouter } from "next/router";
import { useState } from "react";
import { FaShare } from "react-icons/fa";
import SharePopup from "../../components/SharePopup";
import Helmet from "../../containers/Helmet";
import Footer from "../../layout/Footer";
import HeaderV3 from "../../layout/HeaderV3";
import Section from "../../layout/Section";
import { useFetchSWRv2 } from "../../utils/hooks";
import {
  getBlogDetail,
  getHotelBrands,
  getHotelDetail,
  getSitemap,
} from "../../utils/api";
import { IHeader } from "../../utils/types";

const UnsafeHeading = Heading as any;
const UnsafeButton = Button as any;
const UnsafeFlex = Flex as any;
const UnsafeText = Text as any;

export default function SearchPage({
  header,
  hotelBrands,
  sitemapData,
  blog,
}: {
  header: IHeader;
  hotelBrands: any;
  sitemapData: any;
  blog: any;
}) {
  const { locale, query, isReady, push } = useRouter();
  const [isSharePop, setIsSharePop] = useState(false);
  const [blogTemp] = useFetchSWRv2(
    `blog`,
    {
      cct_status: "publish",
      slug: String(query.bid || blog.slug || ""),
    },
    {
      refreshInterval: 10000,
      fallbackData: [blog],
      revalidateOnMount: true,
      revalidateOnFocus: true,
      dedupingInterval: 0,
    }
  );
  const currentBlog = blogTemp?.[0] || blog;

  // const [itemTemp, isLoading, isError] = useFetchSWRv2(`blog`, {
  //   cct_status: "publish",
  //   _ID: query.id,
  // });

  // useEffect(() => {
  //   if (!isReady) return;

  //   if (!query.id || (!isLoading && !itemTemp)) push("/blog");
  // }, [isLoading, isReady, itemTemp, push, query]);

  return (
    <>
      <Helmet
        title={
          !isEmpty(currentBlog.meta_title_id) && locale == "id"
            ? currentBlog.meta_title_id
            : !isEmpty(currentBlog.meta_title_en)
              ? currentBlog.meta_title_en
              : `${currentBlog.title} - Blog | Parador Hotels & Resorts`
        }
        description={
          locale == "id"
            ? !isEmpty(currentBlog.meta_desc_id)
              ? currentBlog.meta_desc_id
              : ""
            : !isEmpty(currentBlog.meta_desc_en)
              ? currentBlog.meta_desc_en
              : ""
        }
        image={
          !isEmpty(currentBlog.meta_thumb)
            ? currentBlog.meta_thumb
            : currentBlog.thumbnail
        }
      />
      <HeaderV3
        headerItem={header}
        isHomepage={false}
        hotelBrands={hotelBrands}
      />

      <Section>
        <UnsafeHeading
          mb={2}
          as="h1"
          fontWeight={400}
          letterSpacing="wider"
          color={header.color_primary}
          textAlign="center"
        >
          {currentBlog.title}
        </UnsafeHeading>

        <Markup
          className="white-space-enter"
          content={
            locale == "id"
              ? currentBlog.content_id?.replace(/\\n/g, `<br>`) ?? ""
              : currentBlog.content_en?.replace(/\\n/g, `<br>`) ?? ""
          }
        />

        {currentBlog.promo_url != "" && currentBlog.promo_url != null && (
          <Center mt={4}>
            <Box>
              <UnsafeButton
                variant="dark"
                onClick={() => {
                  push(currentBlog.promo_url);
                }}
              >
                {locale == "id" ? `Lebih Lanjut` : `Learn More`}
              </UnsafeButton>
            </Box>
          </Center>
        )}

        <Box mt={6}>
          <UnsafeButton
            variant="ghost"
            color={header.color_primary}
            onClick={() => setIsSharePop(true)}
            px={0}
          >
            <UnsafeFlex align="center" gap={2}>
              <FaShare />
              <UnsafeText fontWeight={600}>
                {locale == "id" ? "Bagikan" : "Share"}
              </UnsafeText>
            </UnsafeFlex>
          </UnsafeButton>
        </Box>

        {isSharePop && (
          <SharePopup
            isOpen={isSharePop}
            onClose={() => setIsSharePop(false)}
          />
        )}
      </Section>

      <Box h={10} />
      <Footer
        loc={header.location_url}
        address={header.location_long}
        email={header.email}
        phone={header.phone}
      />
    </>
  );
}

export async function getStaticProps({ locale, params }: any) {
  try {
    let temp;

    const blog: any = await getBlogDetail({
      cct_status: "publish",
      slug: params.bid,
    }).then((r) => r.data?.[0]);

    const data: any = await getHotelDetail({
      is_parador: "true",
      cct_status: "publish",
    }).then((r) => r.data?.[0]);

    // Check if blog or hotel data exists
    if (!blog || !data) {
      return {
        notFound: true,
      };
    }

    temp = await data;

    const sitemapData: any = await getSitemap({
      type: "Privacy Policy",
    }).then((res) => res.data?.[0]);

    if (locale == "id") {
      let x = JSON.stringify(data);
      let y = x.replace(/_id/g, "");
      temp = JSON.parse(y);
    } else {
      let x = JSON.stringify(data);
      let y = x.replace(/_en/g, "");
      temp = JSON.parse(y);
    }

    const {
      _ID,
      name,
      location_short,
      location_url,
      hotel_location,
      phone,
      logo_dark,
      logo_light,
      color_primary,
      color_secondary,
      is_parador,
      slug,
      hotel_code,
      home_headline,
      hotel_sliders,
      hotel_gallery,
      location_short_en,
      has_dining,
      has_wedding,
      has_meeting_events,
      location_long,
      location_long_en,
      email,
    } = temp;

    const header: IHeader = {
      email: email,
      instagram: temp.instagram,
      facebook: temp.facebook,
      location_long: location_long ? location_long : location_long_en,
      id: _ID,
      name,
      color_primary,
      color_secondary,
      location: location_short ? location_short : location_short_en,
      location_url,
      hotel_location,
      phone,
      logo_dark,
      logo_light,
      is_parador: is_parador === "true",
      hotel_code: hotel_code == 0 ? null : hotel_code,
      slug,
      has_dining,
      has_wedding,
      has_meeting_events,
    };

    const hotelBrands = await getHotelBrands();

    return {
      props: {
        header,
        hotelBrands,
        sitemapData,
        blog,
      },
      revalidate: parseInt(process.env.NEXT_PUBLIC_REVALIDATE_TIME ?? "200", 10), // In seconds
    };
  } catch (error) {
    console.error('Error in getStaticProps for blog:', error);
    return {
      notFound: true,
    };
  }
}

export async function getStaticPaths() {
  try {
    const blogs: any = await getBlogDetail({
      cct_status: "publish",
    }).then((r) => r.data);

    const paths = blogs
      .filter((blog: any) => blog.slug && typeof blog.slug === 'string')
      .map((blog: any) => ({
        params: { bid: blog.slug }
      }));

    return {
      paths,
      fallback: "blocking" // Use blocking fallback for non-pregenerated pages
    };
  } catch (error) {
    console.error('Error in getStaticPaths:', error);
    return { paths: [], fallback: "blocking" };
  }
}
